import { formatISO } from "date-fns";
import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "urql";

import { RoundForm } from "@/components/forms/RoundForm";
import { RoundInput, zRoundInput } from "@/components/forms/validation/round";
import { buttonVariants } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { graphql } from "@/gen";
import { InputMaybe, RoundSyncerConfigInput } from "@/gen/graphql";
import { TournamentStageT } from "@/routes/tournament-stage/queries";
import { cn } from "@/utils";

const createRoundMutation = graphql(`
    mutation createRound($input: CreateRoundInput!) {
        createRound(input: $input) {
            __typename
            ...UseRound
        }
    }
`);

export const RoundCreateDialog = () => {
    const [_res, createRound] = useMutation(createRoundMutation);
    const { tournamentStage } = useOutletContext<{
        tournamentStage: TournamentStageT;
    }>();
    const [isOpen, setIsOpen] = useState(false);

    const onSubmit: SubmitHandler<RoundInput> = async input => {
        const roundSyncerConfig: InputMaybe<RoundSyncerConfigInput> =
            input.roundSyncerConfig == null
                ? null
                : {
                      ...input.roundSyncerConfig,
                      pgnProviderType: input.roundSyncerConfig.pgnProviderType.toLowerCase(),
                      pollStart: input.roundSyncerConfig.pollStart
                          ? formatISO(input.roundSyncerConfig.pollStart)
                          : undefined,
                      pollEnd: input.roundSyncerConfig.pollEnd
                          ? formatISO(input.roundSyncerConfig.pollEnd)
                          : undefined,
                  };
        const result = await createRound({
            input: {
                tournamentStageId: tournamentStage.id,
                title: input.title,
                scheduledStartAt: input.scheduledStartAt?.toISOString(),
                lastGameScheduledStartAt: input.lastGameScheduledStartAt?.toISOString() ?? "none",
                roundSyncerConfig,
            },
        });

        if (result.error) {
            toast.error("Unable to save tournament stage", { description: result.error.message });
            for (const error of result.error.graphQLErrors) {
                // @ts-expect-error typed as string from the API
                form.setError(error.name, { message: error.message });
            }
        } else {
            toast.success("Tournament stage was created");
            setIsOpen(false);
        }
    };

    const defaultValues = zRoundInput.parse({
        title: "",
        scheduledStartAt: new Date(),
        lastGameScheduledStartAt: new Date(),
    });

    return (
        <Dialog open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
            <DialogTrigger className={cn(buttonVariants())}>
                <PlusIcon />
            </DialogTrigger>
            <DialogContent
                className="max-h-[80vh] w-full max-w-3xl overflow-y-auto"
                aria-describedby={undefined}
            >
                <DialogHeader>
                    <div className="mr-4 flex items-center justify-between">
                        <DialogTitle>Create round</DialogTitle>
                    </div>
                </DialogHeader>
                <RoundForm onSubmit={onSubmit} defaultValues={defaultValues} />
            </DialogContent>
        </Dialog>
    );
};
